
// src/Components/TestimonialsSection.js
import React, { useState, useEffect } from "react";

/*
  Theme palette (blue & gold):
  --gold        : #C9A53A
  --blue        : #1E3A8A
  --light-blue  : #3B82F6 (accent)
  --light-gray  : #F2F2EE
  --dark        : #1B3D2F (kept for text contrast)
*/

const GOLD       = "#C9A53A";
const BLUE       = "#1E3A8A";
const LIGHT_BLUE = "#3B82F6";
const LIGHT_GRAY = "#F2F2EE";
const DARK       = "#1B3D2F";

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Ndlovu",
      location: "Johannesburg",
      rating: 5,
      text: "Dlams Software Solutions completely modernized our outdated booking system. They understood our workflow from day one and delivered a custom platform that's intuitive and reliable. Communication throughout the project was excellent.",
      service: "Custom Software Development"
    },
    {
      id: 2,
      name: "Thabo Mokoena",
      location: "Cape Town",
      rating: 5,
      text: "We needed an e-commerce store that could handle high traffic during sales season. Dlams built us a fast, secure platform and even helped with the launch. Our online sales have grown significantly since going live.",
      service: "E-commerce Development"
    },
    {
      id: 3,
      name: "Priya Singh",
      location: "Durban",
      rating: 5,
      text: "From our first call, the Dlams team grasped exactly what our business needed in a mobile app. They kept us updated at every stage and delivered a polished product ahead of schedule. We couldn't be happier with the result.",
      service: "Mobile App Development"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const StarRating = ({ rating }) => (
    <div style={styles.starRating}>
      {[...Array(5)].map((_, index) => (
        <span key={index} style={index < rating ? styles.starFilled : styles.starEmpty}>
          ★
        </span>
      ))}
    </div>
  );

  return (
    <section style={styles.container}>
      <style>{`
        .t-nav-btn:hover {
          background: ${GOLD} !important;
          color: ${BLUE} !important;
          transform: scale(1.05);
        }
        .t-dot:hover {
          background: ${GOLD} !important;
          transform: scale(1.2);
        }
        .t-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(27,61,47,0.2) !important;
          border-color: rgba(201,165,58,0.3) !important;
        }
      `}</style>

      <div style={styles.header}>
        <h2 style={styles.title}>What Our Customers Say</h2>
        <p style={styles.subtitle}>
          Real feedback from satisfied clients who trusted us with their digital projects across South Africa
        </p>
      </div>

      <div style={styles.carouselContainer}>
        <div style={styles.carousel}>

          {/* Testimonial Card - matches AdvertisingSection card style */}
          <div className="t-card" style={styles.testimonialCard}>
            <StarRating rating={testimonials[currentTestimonial].rating} />
            <p style={styles.testimonialText}>
              "{testimonials[currentTestimonial].text}"
            </p>
            <div style={styles.customerInfo}>
              <div style={styles.customerDetails}>
                <h4 style={styles.customerName}>{testimonials[currentTestimonial].name}</h4>
                <p style={styles.customerLocation}>{testimonials[currentTestimonial].location}</p>
                <div style={styles.serviceBadge}>
                  {testimonials[currentTestimonial].service}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div style={styles.navigation}>
            <button className="t-nav-btn" style={styles.navButton} onClick={prevTestimonial}>
              ‹
            </button>

            <div style={styles.dots}>
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className="t-dot"
                  style={{
                    ...styles.dot,
                    background: index === currentTestimonial ? GOLD : "rgba(30,58,138,0.25)",
                  }}
                  onClick={() => setCurrentTestimonial(index)}
                />
              ))}
            </div>

            <button className="t-nav-btn" style={styles.navButton} onClick={nextTestimonial}>
              ›
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;

const styles = {
  container: {
    padding: "3rem 1rem",
    background: "transparent", // keep as original, no extra background
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    textAlign: "center",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "clamp(2rem, 4vw, 3rem)",
    fontWeight: 800,
    color: BLUE,
    margin: "0 0 1rem 0",
    background: `linear-gradient(135deg, ${BLUE} 0%, ${LIGHT_BLUE} 30%, ${GOLD} 100%)`,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textShadow: "0 4px 30px rgba(255, 215, 0, 0.2)",
  },
  subtitle: {
    fontSize: "1.1rem",
    color: BLUE,
    maxWidth: "500px",
    margin: "0 auto",
    lineHeight: 1.5,
    opacity: 0.8,
  },
  carouselContainer: {
    maxWidth: "500px",
    margin: "0 auto 2rem",
  },
  carousel: {
    position: "relative",
  },
  testimonialCard: {
    backgroundColor: LIGHT_GRAY,
    borderRadius: "12px",
    padding: "28px 24px",
    boxShadow: "0 12px 32px rgba(27,61,47,0.14)",
    borderTop: `4px solid ${GOLD}`,
    transition: "all 0.25s ease",
    textAlign: "center",
    marginBottom: "1.5rem",
  },
  starRating: {
    marginBottom: "1rem",
  },
  starFilled: {
    color: GOLD,
    fontSize: "1.2rem",
    margin: "0 2px",
    opacity: 1,
  },
  starEmpty: {
    color: "rgba(201,165,58,0.25)",
    fontSize: "1.2rem",
    margin: "0 2px",
  },
  testimonialText: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: DARK,
    margin: "0 0 1.5rem 0",
    fontStyle: "italic",
  },
  customerInfo: {
    display: "flex",
    justifyContent: "center",
  },
  customerDetails: {
    textAlign: "center",
  },
  customerName: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: BLUE,
    margin: "0 0 0.2rem 0",
  },
  customerLocation: {
    color: DARK,
    margin: "0 0 0.5rem 0",
    fontSize: "0.9rem",
  },
  serviceBadge: {
    background: "rgba(30,58,138,0.08)",
    color: BLUE,
    padding: "0.3rem 0.8rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: "600",
    display: "inline-block",
    border: `1px solid rgba(30,58,138,0.2)`,
  },
  navigation: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 1rem",
  },
  navButton: {
    background: "transparent",
    border: `2px solid ${GOLD}`,
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    fontSize: "1.2rem",
    color: GOLD,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  dots: {
    display: "flex",
    gap: "0.5rem",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
};
